/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */
import Box from '@mui/material/Box';
import styled from 'styled-components';
import {
  FlyoutProps,
  LineSegment,
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryLine,
  VictoryVoronoiContainer,
} from 'victory';
import { format } from 'bytes';
import appTheme from '../../common/theme/appTheme.json';
import { AggregationInterval } from '../../../shared/entities/dataUsage';
import dayjs from 'dayjs';
import { isNil, round } from 'lodash';

const baseLabelStyles = {
  fontFamily: appTheme.appFontFamily,
  fontSize: 12,
  lineHeight: 16,
  padding: 10,
  fill: appTheme.mptFormLabelText,
  stroke: 'transparent',
};

const DEFAULT_COLORS = [
  '#A4D810', // green for RL
  '#FFB029', // orange for FL
];

const theme = {
  line: {
    style: {
      labels: baseLabelStyles,
    },
  },
  axis: {
    style: {
      axis: {
        stroke: appTheme.secondaryBackgroundColor,
        strokeWidth: 2,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
      },
      axisLabel: { textAnchor: 'middle', padding: 25 },
      grid: {
        fill: 'none',
        stroke: 'none',
        pointerEvents: 'painted',
      },
      ticks: {
        fill: 'transparent',
        size: 1,
        stroke: 'transparent',
      },
      tickLabels: baseLabelStyles,
    },
  },
};

export const TOOLTIP_WIDTH = 235;
const TOOLTIP_POINTER_SIZE = 10;
const TOOLTIP_LINE_HEIGHT = 24;
const TOOLTIP_LEFT_MARGIN = 12;
const TOOLTIP_BOTTOM_MARGIN = 18;
const TOOLTIP_HORIZONTAL_PADDING = 12.5; // Minimum space (pixels) between edge of tooltip and end of graph

const CHART_HEIGHT = 325;
const CHART_CONTAINER_HEIGHT = 375;
const CHART_CONTAINER_WIDTH = window.innerWidth - 52 - 32; //device_width - drawer_width - padding;
const CHART_BOTTOM_MARGIN = 25;
const CHART_LEFT_MARGIN = 75; // Chart X Position of the left, vertical axis (equal to the distance, in pixels, between the left chart container edge and the vertical axis)
const CHART_RIGHT_MARGIN = TOOLTIP_WIDTH / 4;

const dayjsDateFormat = 'DDMMMYYYY[Z]';

export function dateFormatter(date: Date) {
  return dayjs.utc(date).format(dayjsDateFormat).toUpperCase();
}

function getBucketEndDateString(
  tooltipDate: Date,
  aggregationInterval: AggregationInterval | null | undefined,
  userEndDateMs?: number,
) {

  const startDate = dayjs.utc(tooltipDate);
  const userEndDate = !isNil(userEndDateMs) ? dayjs.utc(userEndDateMs) : undefined;
  const dateSeparator = ' - ';

  let result = '';
  let endDate = undefined;

  switch (aggregationInterval) {
    case 'YEAR':
      endDate = startDate.add(1, 'year').subtract(1, 'day');
      break;
    case 'MONTH':
      endDate = startDate.add(1, 'month').subtract(1, 'day');
      break;
    case 'WEEK':
      endDate = startDate.add(1, 'week').subtract(1, 'day');
      break;
    case 'DAY':
      // Displaying end date with daily aggregation is pointless, do nothing
      break;
    default:
      // Aggregation Interval is null/undefined, do nothing
      break;
  }

  if (!isNil(endDate)) {
    // Compares calculated end date with user-inputted end date (ignores hour, min, sec) and updates endDate accordingly
    endDate = !isNil(userEndDate) && userEndDate.isBefore(endDate, 'day') ? userEndDate : endDate;

    // Sets result to ' - <end_date>'
    result = dateSeparator + endDate.utc().format(dayjsDateFormat).toUpperCase();
  }

  return result;
}

export function CustomTooltip(
  props: FlyoutProps & {
    tooltipNames: string[];
    // Series names in chart render order; used to map active points back to the correct tooltip label/color.
    tooltipSeriesNames?: string[];
    aggregationInterval?: AggregationInterval | null | undefined;
    userEndDateMs?: number;
    serviceAvailability?: boolean;
  },
) {
  const { x, y, datum, tooltipNames, tooltipSeriesNames, aggregationInterval, userEndDateMs, serviceAvailability } =
    props;

  const points = (props as unknown as any).activePoints as any[]; // activePoints is provided in the props but not represented in the type
  const dataPoint = datum as unknown as any;
  const tooltipHeight = (points.length + 1) * TOOLTIP_LINE_HEIGHT + TOOLTIP_BOTTOM_MARGIN; // number of points + the date + bottom margin to get the height

  let xPosition = x;
  let xPointerOffset = 0; // Value to move the tooltip pointer away from tooltip center (TOOLTIP_WIDTH / 2)
  if (x !== undefined && x < TOOLTIP_WIDTH / 2 + TOOLTIP_HORIZONTAL_PADDING) {
    /* Left side of the tooltip (plus padding) will get cut off */

    // Ensures the tooltip is positioned with TOOLTIP_HORIZONTAL_PADDING pixels in between the left side of the tooltip and the left edge of the chart
    xPosition = TOOLTIP_HORIZONTAL_PADDING + TOOLTIP_WIDTH / 2;

    // Calculates the offset between the tooltip center and the data point x position
    xPointerOffset = TOOLTIP_WIDTH / 2 - x + TOOLTIP_HORIZONTAL_PADDING;
  } else if (x !== undefined && x > CHART_CONTAINER_WIDTH - TOOLTIP_WIDTH / 2 - TOOLTIP_HORIZONTAL_PADDING) {
    /* Right side of the tooltip (plus padding) will get cut off */

    // Ensures the tooltip is positioned with TOOLTIP_HORIZONTAL_PADDING pixels in between the right side of the tooltip and right edge of the chart
    xPosition = CHART_CONTAINER_WIDTH - (TOOLTIP_HORIZONTAL_PADDING + TOOLTIP_WIDTH / 2);

    // Calculates the offset between the tooltip center and the data point x position (negative to move the pointer to the right of center, instead of left)
    xPointerOffset = -(TOOLTIP_WIDTH / 2 - (CHART_CONTAINER_WIDTH - x) + TOOLTIP_HORIZONTAL_PADDING);
  }

  let yPosition = y;
  if (y !== undefined && y < tooltipHeight + TOOLTIP_POINTER_SIZE) {
    yPosition = tooltipHeight + TOOLTIP_POINTER_SIZE;
  }

  return (
    <g transform={`translate(${xPosition},${yPosition})`}>
      {/* Tooltip Container */}
      <g transform={`translate(${-(TOOLTIP_WIDTH / 2)},${-(tooltipHeight + 12)})`}>
        {/* Tooltip Pointer */}
        <rect
          width={TOOLTIP_POINTER_SIZE}
          height={TOOLTIP_POINTER_SIZE}
          transform={`translate(${TOOLTIP_WIDTH / 2 - xPointerOffset},${
            tooltipHeight - TOOLTIP_POINTER_SIZE / 2
          }) rotate(45)`}
          fill={appTheme.secondaryBackgroundColor}
        />
        {/* Tooltip Outer Rectangle */}
        <rect width={TOOLTIP_WIDTH} height={tooltipHeight} fill={appTheme.secondaryBackgroundColor} />
        {/* Tooltip Body */}
        {points && (
          <g width={`${TOOLTIP_WIDTH}`} height={`${tooltipHeight}`}>
            <g transform={`translate(${TOOLTIP_LEFT_MARGIN * 2},${TOOLTIP_LINE_HEIGHT})`}>
              <text fill={appTheme.primaryTextColor} fontWeight="bold">{`${
                dateFormatter(dataPoint.x) + getBucketEndDateString(dataPoint.x, aggregationInterval, userEndDateMs)
              }`}</text>
            </g>
            {points.map((pnt, idx) => {
              // Victory can return active points for only a subset of series (e.g., when one series has no data).
              // Resolve by childName first so tooltip rows use the correct series metadata.
              const pointSeriesIndex =
                tooltipSeriesNames && !isNil(pnt.childName) ? tooltipSeriesNames.indexOf(pnt.childName) : -1;
              // Fall back to index-based mapping if series name is unavailable.
              const resolvedSeriesIndex = pointSeriesIndex >= 0 ? pointSeriesIndex : idx;

              return (
                <g
                  key={`${pnt.childName || 'tooltip-series'}-${idx}`}
                  transform={`translate(${TOOLTIP_LEFT_MARGIN},${TOOLTIP_LINE_HEIGHT * (idx + 2)})`}
                >
                  <text fill={DEFAULT_COLORS[resolvedSeriesIndex]} textAnchor="center">
                    &#x25CF;
                  </text>
                  <text fill={appTheme.primaryTextColor} dx={12} textAnchor="center">{`${
                    tooltipNames[resolvedSeriesIndex]
                  }: ${serviceAvailability ? round(pnt.y, 2) + '%' : format(parseInt(pnt.y))}`}</text>
                </g>
              );
            })}
          </g>
        )}
      </g>
    </g>
  );
}

interface DataPoint {
  x: Date; // Date
  y: number; // Value
}

interface Series {
  data: DataPoint[];
  name: string;
  tooltipShortenedName: string;
}

interface UsageLineChartProps {
  series: Series[];

  placeholder?: React.ReactNode;
  showPlaceholder?: boolean;

  aggregationInterval?: AggregationInterval | null | undefined;
  userEndDateMs?: number;

  width: number;
}

function UsageLineChart({
  series,
  placeholder,
  showPlaceholder,
  aggregationInterval,
  userEndDateMs,
  width,
}: UsageLineChartProps) {
  return (
    <Box
      sx={{
        backgroundColor: appTheme.primaryBackgroundColor,
        width: `${width}px`,
        height: `${CHART_CONTAINER_HEIGHT}px`,
      }}
    >
      <Box
        sx={{
          width: `${width}px`,
          height: `${CHART_HEIGHT}px`,
          position: 'relative',
        }}
      >
        <VictoryChart
          theme={theme}
          scale={{ x: 'time' }}
          width={width}
          height={CHART_HEIGHT}
          domainPadding={{ y: [40, 40], x: [0, CHART_RIGHT_MARGIN] }}
          padding={{ top: 0, bottom: CHART_BOTTOM_MARGIN, left: CHART_LEFT_MARGIN, right: 0 }}
          containerComponent={
            <VictoryVoronoiContainer
              voronoiDimension="x"
              mouseFollowTooltips={false}
              activateLabels={false}
              labels={(datum: any) => {
                // This will not be used, but need to include the "labels" prop to make the labelComponent to work
                return 'placeholder';
              }}
              labelComponent={
                <CustomTooltip
                  tooltipNames={series.map(data => data.tooltipShortenedName)}
                  // Keep tooltip's series-name map aligned with the rendered VictoryLine order.
                  tooltipSeriesNames={series.map(data => data.name)}
                  aggregationInterval={aggregationInterval}
                  userEndDateMs={userEndDateMs}
                />
              }
            />
          }
        >
          <VictoryAxis
            fixLabelOverlap
            tickComponent={<LineSegment y1={0} y2={CHART_HEIGHT - CHART_BOTTOM_MARGIN} />}
            tickLabelComponent={showPlaceholder ? <VictoryLabel text={''} /> : <VictoryLabel />}
            style={{
              ticks: { stroke: appTheme.secondaryBackgroundColor },
              tickLabels: {
                fill: appTheme.primaryTextColor,
              },
              axis: { stroke: appTheme.secondaryBackgroundColor },
            }}
          />
          {series.map((seriesData, idx) => (
            <VictoryLine
              key={seriesData.name}
              name={seriesData.name}
              style={{
                data: {
                  stroke: DEFAULT_COLORS[idx],
                  strokeWidth: 2,
                },
              }}
              data={seriesData.data}
            />
          ))}

          <VictoryAxis
            dependentAxis
            tickComponent={undefined}
            tickFormat={tickValue => {
              return `${format(tickValue)}`;
            }}
            style={{
              ticks: { stroke: 'transparent' },
              tickLabels: {
                fill: appTheme.primaryTextColor,
              },
              axis: { stroke: appTheme.secondaryBackgroundColor },
            }}
          />
        </VictoryChart>
        {!!showPlaceholder && !!placeholder && placeholder}
      </Box>
      <Box sx={{ width: `${width}px`, height: `${CHART_CONTAINER_HEIGHT - CHART_HEIGHT}px` }}>
        <Box
          sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', height: '100%' }}
        >
          {series.map((seriesData, idx) => (
            <Box key={seriesData.name} sx={{ marginLeft: idx > 0 ? '32px' : 0, display: 'flex', flexDirection: 'row' }}>
              <LegendCircle color={DEFAULT_COLORS[idx]}>&#x25CF;</LegendCircle>
              <LegendTitle>{seriesData.name}</LegendTitle>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default UsageLineChart;

const LegendCircle = styled.div`
  font-size: 20px;
  line-height: 18px;
  color: ${({ color }: { color: string }) => color};
  margin-right: 8px;
`;

const LegendTitle = styled.div`
  font-family: ${appTheme.appFontFamily};
  font-size: 14px;
  line-height: 20px;
  color: ${appTheme.primaryTextColor};
`;
